import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  ContentUnderstandingInsightsData,
  ContentUnderstandingInsightsMetadata,
  ProjectInsightsMetadata,
} from '@scale/shared/types/contentUnderstanding/contentUnderstandingInsights';
import { RedashOverviewDashboard } from '@scale/shared/types/endpoints/customer/overview';

export interface ContentUnderstandingInsightsDataResult {
  body: ContentUnderstandingInsightsData;
}

interface ContentUnderstandingInsightsMetadataResult {
  body: ContentUnderstandingInsightsMetadata;
}

export async function getContentUnderstandingInsightsData(
  projectId: string,
  filename: string,
): Promise<ContentUnderstandingInsightsMetadataResult | ContentUnderstandingInsightsDataResult> {
  const contentUnderstandingInsightsData = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard-insights/fetch`,
    {
      method: 'POST',
      body: { projectId, filename },
    },
  );
  return contentUnderstandingInsightsData;
}

export async function storeContentUnderstandingInsightsData(
  projectId: string,
  insightsFilename: string,
  s3InsightsKey: string,
  data: ContentUnderstandingInsightsData,
  batchName: string,
  projectName: string,
): Promise<ContentUnderstandingInsightsDataResult> {
  const { body: response } = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard-insights/store`,
    {
      method: 'POST',
      body: { projectId, insightsFilename, s3InsightsKey, data, batchName, projectName },
    },
  );
  return response;
}

export async function storeContentUnderstandingCustomizationJSON(
  projectId: string,
  value: ProjectInsightsMetadata,
) {
  const { body: response } = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard-insights/store-customization-json/${projectId}`,
    {
      method: 'POST',
      body: { value },
    },
  );
  return response;
}

export async function getContentUnderstandingCustomizationJSON(
  projectId: string,
): Promise<ProjectInsightsMetadata> {
  const { body: customizationJSON } = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard-insights/fetch-customization-json/${projectId}`,
    {
      method: 'GET',
    },
  );
  return customizationJSON;
}

export async function refreshContentUnderstandingInsightsData(projectId: string): Promise<string> {
  const { body: executionArn } = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard-insights/refresh/${projectId}`,
    {
      method: 'POST',
    },
  );
  return executionArn;
}

export async function isInsightsDashboardConfigEnabled(projectId?: string): Promise<boolean> {
  if (!projectId) {
    return false;
  }
  const textCollectionInsightsDashboardEnabled = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard-insights/is-config-enabled/${projectId}`,
    { method: 'GET' },
  );
  return textCollectionInsightsDashboardEnabled ?? false;
}

export async function isTaxonomyManagementConfigEnabled(projectId?: string): Promise<boolean> {
  if (!projectId) {
    return false;
  }

  const textCollectionTaxonomyManagementEnabled = await apiFetch(
    null,
    `internal/contentUnderstanding/taxonomy-management/is-config-enabled/${projectId}`,
    { method: 'GET' },
  );
  return textCollectionTaxonomyManagementEnabled ?? false;
}

export async function getTrendExplorerData(
  batchId: string,
  customerName: string,
): Promise<ContentUnderstandingInsightsMetadataResult | ContentUnderstandingInsightsDataResult> {
  const trendExplorerData = await apiFetch(
    null,
    `internal/contentUnderstanding/trend-explorer/fetch/${customerName}/${batchId}`,
    { method: 'GET' },
  );
  return trendExplorerData;
}

export async function getPresentationDashboard(pageAddress: string): Promise<{
  pageName: string;
  embeddingUrl: string;
  redashDashboards?: { [key: string]: RedashOverviewDashboard };
}> {
  const presentationDashboards = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard/presentations/${pageAddress}`,
    { method: 'GET' },
  );
  return presentationDashboards;
}

export async function getPresentationDashboardPages(): Promise<
  {
    pageName: string;
    pageAddress: string;
    embeddingUrl: string;
  }[]
> {
  const presentationDashboards = await apiFetch(
    null,
    `internal/contentUnderstanding/dashboard/presentations`,
    { method: 'GET' },
  );
  return presentationDashboards;
}
